import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { fetchGetEmployerUsers } from 'src/redux/users'
import { validateEmail } from 'data/helpers'

const NEW_USER_VALUE = '__new__'

const EmployerKioskRecruiterStep = ({ wizardData, setWizardData, onNext, onBack }) => {
  const dispatch = useDispatch()
  const { users = [] } = useSelector(state => state.users)

  const [selectedUserId, setSelectedUserId] = useState(
    wizardData.employerUserId ? String(wizardData.employerUserId) : '',
  )
  const [showNewUserForm, setShowNewUserForm] = useState(!wizardData.employerId)

  const employerUsers = wizardData.employerId
    ? users.filter(user => user.employerId === wizardData.employerId)
    : []

  useEffect(() => {
    if (wizardData.employerId) {
      dispatch(fetchGetEmployerUsers())
    }
  }, [dispatch, wizardData.employerId])

  useEffect(() => {
    if (wizardData.employerId && users.length >= 0 && employerUsers.length === 0) {
      setShowNewUserForm(true)
    }
  }, [wizardData.employerId, users, employerUsers.length])

  const handleRadioChange = value => {
    setSelectedUserId(value)
    if (value === NEW_USER_VALUE) {
      setShowNewUserForm(true)
      setWizardData(prev => ({ ...prev, employerUserId: null }))
    } else {
      setShowNewUserForm(false)
      const user = employerUsers.find(u => String(u.id) === value)
      if (user) {
        setWizardData(prev => ({
          ...prev,
          employerUserId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        }))
      }
    }
  }

  const handleFieldChange = (name, value) => {
    setWizardData(prev => ({ ...prev, [name]: value }))
  }

  const isNewUserFormValid =
    (wizardData.firstName || '').trim().length > 0 &&
    (wizardData.lastName || '').trim().length > 0 &&
    validateEmail(wizardData.email || '')

  const isNextEnabled = wizardData.employerId
    ? (selectedUserId && selectedUserId !== NEW_USER_VALUE) || (showNewUserForm && isNewUserFormValid)
    : isNewUserFormValid

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Recruiter Identification
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Select yourself from the list or enter your information.
      </Typography>

      {wizardData.employerId && employerUsers.length > 0 && (
        <RadioGroup
          value={selectedUserId || ''}
          onChange={e => handleRadioChange(e.target.value)}
          sx={{ mb: 2 }}
        >
          {employerUsers.map(user => (
            <FormControlLabel
              key={user.id}
              value={String(user.id)}
              control={<Radio />}
              label={`${user.firstName} ${user.lastName} (${user.email})`}
            />
          ))}
          <FormControlLabel
            value={NEW_USER_VALUE}
            control={<Radio />}
            label="I'm not listed"
          />
        </RadioGroup>
      )}

      {showNewUserForm && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label='First Name'
            value={wizardData.firstName || ''}
            onChange={e => handleFieldChange('firstName', e.target.value)}
            fullWidth
            size='small'
          />
          <TextField
            label='Last Name'
            value={wizardData.lastName || ''}
            onChange={e => handleFieldChange('lastName', e.target.value)}
            fullWidth
            size='small'
          />
          <TextField
            label='Email'
            value={wizardData.email || ''}
            onChange={e => handleFieldChange('email', e.target.value)}
            fullWidth
            size='small'
            error={!!wizardData.email && !validateEmail(wizardData.email)}
            helperText={!!wizardData.email && !validateEmail(wizardData.email) ? 'Enter a valid email address' : ''}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack}>Back</Button>
        <Button variant='contained' onClick={onNext} disabled={!isNextEnabled}>
          Next
        </Button>
      </Box>
    </Box>
  )
}

export default EmployerKioskRecruiterStep
